@use '~/vendor/cxy/scss/styles/index.scss' as cxy;
@use 'sass:map';

$caption-height: 40px;
$row-height: 23px;
$scrollBarSize: var(--cxy-spacing-xxxsm);

$divider: 1px solid #3e6175;
$background-color: #294f66;

$z-index-bottom: 1;
$z-index-middle: 2;
$z-index-top: 3;

.wrapper {
  position: relative;
  background-color: $background-color;
  border-radius: var(--cxy-border-radius-m);
  overflow: hidden;

  table {
    overflow: auto;
  }

  .captionWrapper {
    position: sticky;
    z-index: $z-index-top;
    inset-inline: 0 0;
    inset-block-start: 0;
    height: $caption-height;
    display: flex;
    justify-content: space-between;
    padding: var(--cxy-spacing-xxsm);
    background-color: $background-color;

    .caption {
      margin: 0;
      color: var(--cxy-color-text-default-body);
      font-size: var(--cxy-fontSize-s);
      font-weight: var(--cxy-fontWeight-bold);
    }

    .buttonWrapper {
      display: flex;
      align-items: center;
      gap: var(--cxy-spacing-5);

      .button {
        &:first-of-type {
          transform: rotate(-90deg);
        }

        &:last-of-type {
          transform: rotate(90deg);
        }

        svg {
          height: 10px;
        }
      }
    }
  }

  .innerWrapper {
    border-radius: var(--cxy-border-radius-m);
    background-color: $background-color;
    white-space: nowrap;
    overflow-x: hidden;

    &.hasMaxHeight {
      // (Row and border heights) + heading row + border + footer row + border + scrollbar
      height: calc(calc(var(--maxRows) * ($row-height + 2px)) + $row-height + $row-height + 2px + $scrollBarSize);
    }
  }
}

@mixin scrollShadow($position) {
  $top: calc($caption-height + $row-height);
  $bottom: calc($scrollBarSize + $row-height);
  $left: calc(var(--cellWidth) * var(--pinCount));
  $right: $scrollBarSize;
  $size-small: var(--cxy-spacing-xxsm);
  $size-large: 30px;
  $color-dark: color-mix(in srgb, $background-color 90%, var(--cxy-color-icon-black)) 10%;
  $color-background: $background-color;

  $positions: (
    'top': (
      'direction': bottom,
      'position': $top $right auto $left,
      'size': height,
      'size-variable': $size-small,
      'color': $color-dark,
    ),
    'bottom': (
      'direction': top,
      'position': auto $right $row-height $left,
      'size': height,
      'size-variable': $size-small,
      'color': $color-dark,
    ),
    'left': (
      'direction': right,
      'position': $caption-height auto $scrollBarSize $left,
      'size': width,
      'size-variable': $size-small,
      'color': $color-dark,
    ),
    'right': (
      'direction': left,
      'position': $caption-height $right $scrollBarSize auto,
      'size': width,
      'size-variable': $size-large,
      'color': $color-background,
    ),
  );

  $position-map: map.get($positions, $position);
  $direction: map.get($position-map, 'direction');
  $position: map.get($position-map, 'position');
  $size: map.get($position-map, 'size');
  $size-variable: map.get($position-map, 'size-variable');
  $color: map.get($position-map, 'color');

  content: '';
  position: absolute;
  z-index: $z-index-top;
  background-image: linear-gradient(to $direction, $color, transparent);
  inset: $position;
  #{$size}: $size-variable;
}

.hasScrollShadowEnd:after {
  @include scrollShadow(right);
}

.hasScrollShadowStart:before {
  @include scrollShadow(left);
}

.hasScrollShadowTop:before {
  @include scrollShadow(top);
}

.hasScrollShadowBottom:after {
  @include scrollShadow(bottom);
}

.wrapper.hasScrollShadowTop .hasScrollShadowStart:before {
  inset-block-start: $row-height;
}

.wrapper.hasScrollShadowBottom .hasScrollShadowStart:before {
  inset-block-end: calc($scrollBarSize + $row-height);
}

.wrapper:not(.hasVerticalScroll) .hasScrollShadowEnd:after {
  inset-inline-end: 0;
}

.wrapper:not(.hasHorizontalScroll) .hasScrollShadowBottom:after {
  inset-block-end: var(--cellWidth);
}

.hasScrollShadowTop.scrollFullWidth:before,
.hasScrollShadowBottom.scrollFullWidth:after {
  inset-inline-start: 0;
}

// -webkit-appearance:;
::-webkit-scrollbar {
  width: $scrollBarSize;
  height: $scrollBarSize;
}

// /* Handle */
::-webkit-scrollbar-thumb {
  background: #95a5af;
  border-radius: 3px;
}

//the bottom corner of the scrollbar, where both horizontal and vertical scrollbars meet.
::-webkit-scrollbar-corner {
  background: transparent;
}
